import { AuxiliaryDataBundle } from '@core/models/entities/entity-visualization-interface';
import {
  PlanWatchHolder,
  RENDERED_FLAVOURS,
  WatchOverlay,
  WatchPatch,
  WatchPatchEvent
} from '@core/models/plan-edit.model';

import type { PlanWatchState } from './plan-edit.state';

export const EMPTY_WATCH_OVERLAY: WatchOverlay = {
  upserted: {},
  entityTypes: {},
  removed: {}
};

/**
 * Orders a session's states. DL answers `recomputed` at the revision of the `applied` it settles,
 * so a revision alone cannot tell a recompute from the edit before it: the unsettled state of a
 * revision comes first, its settled state second.
 */
export function settledness(revision: number, recomputePending: boolean): number {
  return revision * 2 + (recomputePending ? 0 : 1);
}

/** Uids are user data: a plain index would read `constructor` off Object.prototype. */
function own<V>(record: Record<string, V> | undefined, key: string): V | undefined {
  return record !== undefined && Object.hasOwn(record, key) ? record[key] : undefined;
}

/**
 * DL names entity types in SCREAMING_SNAKE (`ROUTE`) while the entity registry lowercases the
 * catalogue's names (`route`), so removals are stored and looked up under one casing.
 */
function typeKey(entityType: string): string {
  return entityType.toLowerCase();
}

export function applyWatchPatch(overlay: WatchOverlay, patch: WatchPatch): WatchOverlay {
  if (patch.upserted.length === 0 && patch.removed.length === 0) return overlay;
  const upserted = { ...overlay.upserted };
  const entityTypes = { ...overlay.entityTypes };
  const removed = { ...overlay.removed };

  for (const u of patch.upserted) {
    if (!RENDERED_FLAVOURS.includes(u.userEntityType)) {
      console.warn(`[plan watch] ignoring entity type ${u.userEntityType}`);
      continue;
    }
    upserted[u.userEntityType] = { ...own(upserted, u.userEntityType), [u.uid]: u.data };
    const type = typeKey(u.entityType);
    entityTypes[u.userEntityType] = type;
    const gone = own(removed, type);
    if (gone?.includes(u.uid)) removed[type] = gone.filter((uid) => uid !== u.uid);
  }

  for (const r of patch.removed) {
    const type = typeKey(r.entityType);
    for (const [flavour, entityType] of Object.entries(entityTypes)) {
      const byUid = own(upserted, flavour);
      if (entityType !== type || own(byUid, r.uid) === undefined) continue;
      const rest = { ...byUid };
      delete rest[r.uid];
      upserted[flavour] = rest;
    }
    const gone = own(removed, type) ?? [];
    if (!gone.includes(r.uid)) removed[type] = [...gone, r.uid];
  }

  return { upserted, entityTypes, removed };
}

export function upsertedRouteUids(patch: WatchPatch): string[] {
  return patch.upserted.filter((u) => u.userEntityType === 'UserRoute').map((u) => u.uid);
}

export function removedRouteUids(patch: WatchPatch): string[] {
  return patch.removed
    .filter((r) => r.entityType.toLowerCase() === 'route')
    .map((r) => r.uid);
}

/**
 * A snapshot that continues the session on screen rather than one that started while this tab
 * missed the end of the last: same holder, and no older than the state already shown.
 */
export function continuesLiveSession(
  watch: PlanWatchState,
  holder: PlanWatchHolder,
  revision: number
): boolean {
  return (
    watch.phase === 'live' &&
    watch.holder?.userId === holder.userId &&
    watch.holder.via === holder.via &&
    revision >= watch.revision
  );
}

/** Applies a session state newer than the one shown; an older or equal one changes nothing. */
export function advance(watch: PlanWatchState, event: WatchPatchEvent): PlanWatchState {
  if (
    settledness(event.revision, event.recomputePending) <=
    settledness(watch.revision, watch.recomputePending)
  ) {
    return watch;
  }
  return {
    ...watch,
    revision: event.revision,
    recomputePending: event.recomputePending,
    recomputeFailed: false,
    findings: event.findings,
    planKpis: event.planKpis ?? watch.planKpis,
    overlay: applyWatchPatch(watch.overlay, event.patch)
  };
}

/**
 * Takes one state of `planId`'s session into the watch, whichever message brought it: while a watch
 * is in flight it waits for the snapshot, which `advance` then orders it against.
 */
export function feedWatch(
  watch: PlanWatchState,
  planId: string | null,
  event: WatchPatchEvent
): PlanWatchState {
  if (planId === null || planId !== watch.planId) return watch;
  if (watch.awaitingSnapshot) {
    return { ...watch, pendingPatches: [...watch.pendingPatches, event] };
  }
  return watch.phase === 'live' ? advance(watch, event) : watch;
}

export function overlaidRaw(
  overlay: WatchOverlay,
  versionName: string,
  uid: string
): Record<string, unknown> | undefined {
  return own(own(overlay.upserted, versionName), uid);
}

function removedUids(overlay: WatchOverlay, entityType: string | undefined): Set<string> {
  return new Set(
    entityType === undefined ? [] : (own(overlay.removed, typeKey(entityType)) ?? [])
  );
}

/**
 * Lays the overlay over one flavour's rows: an overlaid row is replaced where it stands, a removed
 * one leaves, and each of `insertedUids` the overlay holds and the rows do not is appended. Only
 * the caller knows which entities the session created: the overlay also holds rows of other pages.
 * Returns `rows` itself when the overlay does not touch them, so signals reading the result see no
 * change.
 */
export function overlayEntityMap<E extends { uid: string }>(
  rows: Map<string, E>,
  overlay: WatchOverlay,
  versionName: string,
  entityType: string | undefined,
  wrap: (raw: Record<string, unknown>) => E | undefined,
  insertedUids: readonly string[] = []
): Map<string, E> {
  const removed = removedUids(overlay, entityType);
  if (own(overlay.upserted, versionName) === undefined && removed.size === 0) return rows;
  let changed = false;
  const out = new Map<string, E>();
  for (const [uid, row] of rows) {
    if (removed.has(uid)) {
      changed = true;
      continue;
    }
    const raw = overlaidRaw(overlay, versionName, uid);
    const wrapped = raw === undefined ? undefined : wrap(raw);
    if (wrapped !== undefined) changed = true;
    out.set(uid, wrapped ?? row);
  }
  for (const uid of insertedUids) {
    if (rows.has(uid) || removed.has(uid)) continue;
    const raw = overlaidRaw(overlay, versionName, uid);
    const wrapped = raw === undefined ? undefined : wrap(raw);
    if (wrapped === undefined) continue;
    out.set(uid, wrapped);
    changed = true;
  }
  return changed ? out : rows;
}

/**
 * The aux bundle as the session has it, for the flavours `shows` lets through: removals are keyed
 * by entity type, which flavours share. The given bundle is cached upstream and stays untouched.
 */
export function overlayAuxBundle(
  bundle: AuxiliaryDataBundle,
  overlay: WatchOverlay,
  shows: (versionName: string) => boolean,
  entityTypeFor: (versionName: string) => string | undefined
): AuxiliaryDataBundle {
  const entitiesArrayPerVersionName: Record<string, unknown[]> = {};
  for (const [versionName, rows] of Object.entries(bundle.entitiesArrayPerVersionName)) {
    if (!shows(versionName)) {
      entitiesArrayPerVersionName[versionName] = rows;
      continue;
    }
    const removed = removedUids(overlay, entityTypeFor(versionName));
    entitiesArrayPerVersionName[versionName] = rows
      .filter((row) => !removed.has(String((row as { uid?: unknown }).uid)))
      .map(
        (row) =>
          overlaidRaw(overlay, versionName, String((row as { uid?: unknown }).uid)) ?? row
      );
  }
  return { entitiesArrayPerVersionName };
}
